import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import getLeads from '../actions/GetLeads';
import viewLead from '../actions/ViewLead';
import getMoreLeads from '../actions/GetMoreLeads';
import OpportunityCard from '../components/cards/leads/opportunity-card';
import AcceptedLeadCard from '../components/cards/leads/accepted-lead-card';
import DataList from '../components/DataList.jsx';
import CmsFooter from '../components/CmsFooter.jsx';
import FeatureList from '../components/FeatureList';
import showFeatureList from '../actions/ShowFeatureList';
import hideFeatureList from '../actions/HideFeatureList';
import getFeatures from '../actions/GetFeatures';
import {decodeHtmlEntities} from "./utils";
import SoftwareInterestComponent from "../components/SoftwareInterestComponent";
import InlineLeadViewer from "../components/InlineLeadViewer";
import SoftwareMarketComponent from "../components/SoftwareMarketComponent";
import moment from "moment";
import TeamLeadPurchaseLimit from '../components/TeamLeadPurchaseLimit'
import CustomCheckbox from '../components/cards/leads/CustomCheckbox';

let ignoreAutoloading = false;

// Renders a different component if the page is "archived"
const FeedRouter = (props) => {
  if (props.page === "archived") {
    return <FeedArchived {...props} />
  }

  return <Feed {...props} />
}

// Renders archived leads
const FeedArchived = ({ history, viewLead }) => {
  const [leads, setLeads] = useState([]);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsAvailable, setLeadsAvailable] = useState(true);
  const [leadsLoading, setLeadsLoading] = useState(true);

  const load = (page) => {
    setLeadsLoading(true);

    const request = {
      method: "get",
      headers: {
        "accept": "application/json",
      }
    }

    let params = new URLSearchParams()
    params.set("type", "archived");
    params.set("page", page.toString());

    fetch("/api/leads?" + params, request).then((response) => {
      return response.json()
    }).then((data) => {
      setLeads((current) => [...current, ...data.leads]);
      setLeadsPage(data.page);
      setLeadsAvailable(data.available);
      setLeadsLoading(false);
    });
  };

  const loadMore = () => {
    if (leadsAvailable) {
      load(leadsPage + 1);
    }
  };

  useEffect(() => {
    load(1);
  }, []);

  let contentsLabel = "Archived leads are schedule for backup";

  if (leadsLoading) {
    contentsLabel = "Loading leads ...";
  }

  let contentsRows = leads.map((lead) => {
    let revenue = "N/A";
    let market = "N/A";
    let received = "N/A";

    if (lead.fields.annual_revenue) {
      revenue = lead.fields.annual_revenue;
    }

    if (lead.fields.market) {
      market = lead.fields.market;
    }

    if (lead.combined_date) {
      received = moment(lead.combined_date).format("MM/DD");
    }

    return (
      <tr key={lead.id} className="interactive" onClick={() => viewLead(lead.id, false, true)}>
        <td></td>
        <td>{lead.fields.company_name}</td>
        <td>{revenue}</td>
        <td>{market}</td>
        <td>{received}</td>
      </tr>
    )
  });

  let contentsTable = null;

  if (contentsRows.length > 0) {
    contentsTable = (
      <table className="lead-table">
        <thead>
        <tr>
          <th></th>
          <th>Archived Leads</th>
          <th>Revenue</th>
          <th>Market</th>
          <th>Received</th>
        </tr>
        </thead>
        <tbody>
        {contentsRows}
        </tbody>
      </table>
    );
  }

  if (contentsRows.length < 1 && !leadsAvailable) {
    contentsTable = (
      <p className="text-1">No archived leads are currently available</p>
    );
  }

  let contentsButton = null;

  if (leads.length > 0 && leadsAvailable) {
    let label = 'Load more archived leads';
    let style = {};

    if (leadsLoading) {
      label = 'Loading ...';
      style = {
        cursor: 'default',
        pointerEvents: 'none',
        opacity: '0.5',
      };
    }

    contentsButton = (
      <button onClick={loadMore} className="btn btn--neutral btn--block" style={style}>
        {label}
      </button>
    );
  }

  const navigateToPurchased = () => {
    history.push('/leads/purchased');
  };

  return (
    <section className="l-wrapper-container">
      <header className="l-leads-header flex-x">
        <h1 className="t-primary-header flex-1">Your Archived Leads</h1>
        <div className="btn button-alt" onClick={navigateToPurchased}>View Purchased Leads</div>
      </header>
      <div>
        <p className="text-2">{contentsLabel}</p>
      </div>
      <div>
        {contentsTable}
      </div>
      <div>
        {contentsButton}
      </div>
      <CmsFooter/>
      <InlineLeadViewer archived />
    </section>
  );
}

// Renders available and purchased leads
const Feed = ({
  page,
  leads,
  team,
  opportunityCount,
  features,
  match,
  getLeads,
  getMoreLeads,
  showFeaturesList,
  hideFeaturesList,
  viewLead,
  lead_id,
  history,
}) => {
  // State variables
  const [currentOrder, setCurrentOrder] = useState('');
  const [type, setType] = useState('');
  const [qualificationType, setQualificationType] = useState('');
  const [feedTitle, setFeedTitle] = useState('');
  const [selectedCountries, setSelectedCountries] = useState(new Set());
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [selectedIndustrySectors, setSelectedIndustrySectors] = useState(new Set());
  const [showIndustrySectorDropdown, setShowIndustrySectorDropdown] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState(new Set());
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const [selectedProblems, setSelectedProblems] = useState(new Set());
  const [showProblemDropdown, setShowProblemDropdown] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [selectedMarket, setSelectedMarket] = useState(0);
  const [showQualificationDropdown, setShowQualificationDropdown] = useState(false);
  const [selectedQualificationTypes, setSelectedQualificationTypes] = useState(new Set());

  const handleCategoryChange = (selected) => {
    if (selected.size === 0 || selected.has('All')) {
      setSelectedCategories(new Set()); // Reset to show all leads
    } else {
      setSelectedCategories(selected);
    }
  };

  useEffect(() => {
    if (window !== window.parent || window.location.search.includes('source=em')) {
      setTimeout(() => {
        viewLead(lead_id, window.location.search.includes('source=em') === false);
      }, 0);
      return;
    }
    if (leads.data.length > 0 && lead_id !== undefined && window.directLeadViewed === undefined) {
      setTimeout(() => {
        viewLead(lead_id, true);
      }, 0);
      window.directLeadViewed = lead_id;
    }
  }, [lead_id, leads.data]);

  /*
  useEffect(() => {
    const filteredCountries = extractCountriesFromLeads();
    const filteredIndustrySectors = extractIndustrySectorsFromLeads();
    const filteredProblems = extractProblemsFromLeads();
    // Update dropdowns with filtered countries, problems and industry sectors
  }, [selectedCategories, leads.data, extractCountriesFromLeads, extractIndustrySectorsFromLeads, extractProblemsFromLeads]);
  */

  useEffect(() => {
    if (page === "available" && team && team.primary_market_id && selectedMarket < 1) {
      setSelectedMarket(team.primary_market_id);
    }
  }, [page, team]);

  // Refs for handling outside clicks
  const countryDropdownRef = useRef(null);
  const dropdownToggleButtonRef = useRef(null);
  const industrySectorDropdownRef = useRef(null);
  const industrySectorDropdownToggleButtonRef = useRef(null);
  const specialtyDropdownRef = useRef(null);
  const specialtyDropdownToggleButtonRef = useRef(null);
  const problemDropdownRef = useRef(null);
  const problemDropdownToggleButtonRef = useRef(null);
  const qualificationDropdownRef = useRef(null);

  // Fetch leads on page load and set up event listeners on component mount
  useEffect(() => {
    if (window !== window.parent) {
      return;
    }

    if (match && match.params && match.params.lead_id) {
      viewLead(match.params.lead_id);
    }

    const params = getParams(page);
    getLeads(params);

    window.scroll(0, 0);
    document.addEventListener('mousedown', handleOutsideClick);
    // window.addEventListener('scroll', onScroll);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      // window.removeEventListener('scroll', onScroll);
    };
  }, [page, getLeads]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    }
  }, [leads, onScroll]);

  // Function to handle clicks outside the dropdown to close it
  const handleOutsideClick = (event) => {
    if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target) && event.target !== dropdownToggleButtonRef.current) {
      setShowCountryDropdown(false);
    }
    if (
      industrySectorDropdownRef.current &&
      !industrySectorDropdownRef.current.contains(event.target) &&
      event.target !== industrySectorDropdownToggleButtonRef.current
    ) {
      setShowIndustrySectorDropdown(false);
    }
    if (
      specialtyDropdownRef.current &&
      !specialtyDropdownRef.current.contains(event.target) &&
      event.target !== specialtyDropdownToggleButtonRef.current
    ) {
      setShowSpecialtyDropdown(false);
    }
    if (
      problemDropdownRef.current &&
      !problemDropdownRef.current.contains(event.target) &&
      event.target !== problemDropdownToggleButtonRef.current
    ) {
      setShowProblemDropdown(false);
    }
    if (
      qualificationDropdownRef.current &&
      !qualificationDropdownRef.current.contains(event.target) &&
      event.target !== qualificationDropdownRef.current
    ) {
      setShowQualificationDropdown(false);
    }

  };

  // Function to get the parameters for the feed based on the page type
  const getParams = (page) => {
    let params = {};
    let feedTitle = '';

    switch (page) {
      case 'purchased':
        document.title = 'Purchased Leads | Prospect Path';
        feedTitle = 'Purchased Leads';
        params = {
          type: 'accepted',
          // qualification_type: ['phone_qualified', 'mql_plus'],
          order_by: 'combined_date',
        };
        break;
      case 'available':
        document.title = 'Available Leads | Prospect Path';
        feedTitle = 'Available Leads';
        params = {
          type: 'opportunity',
          order_by: currentOrder || 'combined_date',
        };
        break;
      default:
        document.title = 'Leads | Prospect Path';
        feedTitle = 'Company Type';
        params = {
          order_by: 'combined_date',
          type: 'accepted,opportunity',
          qualification_type: null,
        };
    }

    setFeedTitle(feedTitle);
    setCurrentOrder(params.order_by);
    setType(params.type);
    setQualificationType(params.qualification_type);
    return params;
  };

  // Function to handle infinite scroll
  const onScroll = useCallback(() => {
    if (!type) {
      return;
    }

    if (ignoreAutoloading) {
      return;
    }

    if (
      (window.innerHeight + window.scrollY) >= (document.body.offsetHeight - 1000) &&
      !leads.loading_more
    ) {
      loadMoreLeads();
      ignoreAutoloading = true;
    }
  }, [type, leads.loading_more]);

  useEffect(() => {
    ignoreAutoloading = false;
  }, [leads]);

  // Function to load more leads
  const loadMoreLeads = useCallback(() => {
    if (!leads.loading_more && leads.next_page_url) {
      const params = {
        ...leads.params,
        type,
      };
      getMoreLeads(params);
    }
  }, [leads.loading_more, leads.next_page_url, leads.params, type]);

  // Function to update the order of the leads
  const updateOrder = useCallback((value) => {
    if (!leads.params) return;

    const params = {...leads.params, order_by: value};
    setCurrentOrder(value);
    getLeads(params);
  }, [leads.params, getLeads]);

  // Function to handle the order change
  const orderChanged = useCallback((event) => {
    updateOrder(event.target.value);
  }, [updateOrder]);

  // Function to toggle filters dropdown visibility
  const toggleCountryDropdown = useCallback(() => {
    setShowCountryDropdown((prev) => !prev);
  }, []);

  const toggleIndustrySectorDropdown = useCallback(() => {
    setShowIndustrySectorDropdown((prev) => !prev);
  }, []);

  const toggleSpecialtyDropdown = useCallback(() => {
    setShowSpecialtyDropdown((prev) => !prev);
  }, []);

  const toggleProblemDropdown = useCallback(() => {
    setShowProblemDropdown((prev) => !prev);
    }, []);

  const toggleQualificationDropdown = useCallback(() => {
    setShowQualificationDropdown((prev) => !prev);
  }, []);

  // Functions to handle the change in filters selection
  const countryChanged = useCallback((event) => {
    const country = event.target.value;
    setSelectedCountries((prev) => {
      const updatedCountries = new Set(prev);
      if (updatedCountries.has(country)) {
        updatedCountries.delete(country);
      } else {
        updatedCountries.add(country);
      }
      return updatedCountries;
    });
    event.target.nextElementSibling.classList.toggle('checked');
  }, []);

  const industrySectorChanged = useCallback((event) => {
    const industrySector = event.target.value;
    setSelectedIndustrySectors((prev) => {
      const updatedIndustrySectors = new Set(prev);
      if (updatedIndustrySectors.has(industrySector)) {
        updatedIndustrySectors.delete(industrySector);
      } else {
        updatedIndustrySectors.add(industrySector);
      }
      return updatedIndustrySectors;
    });
    event.target.nextElementSibling.classList.toggle('checked');
  }, []);

  const specialtyChanged = useCallback((event) => {
    const specialty = event.target.value;
    setSelectedSpecialties((prev) => {
      const updatedSpecialties = new Set(prev);
      if (updatedSpecialties.has(specialty)) {
        updatedSpecialties.delete(specialty);
      } else {
        updatedSpecialties.add(specialty);
      }
      return updatedSpecialties;
    });
    event.target.nextElementSibling.classList.toggle('checked');
  }, []);

    const problemChanged = useCallback((event) => {
    const problem = event.target.value;
    setSelectedProblems((prev) => {
        const updatedProblems = new Set(prev);
        if (updatedProblems.has(problem)) {
            updatedProblems.delete(problem);
        } else {
            updatedProblems.add(problem);
        }
        return updatedProblems;
    });
    event.target.nextElementSibling.classList.toggle('checked');
    }, []);

  const qualificationTypeChanged = useCallback((event) => {
    const qualification = event.target.value;
    setSelectedQualificationTypes((prev) => {
      const updatedQualifications = new Set(prev);
      if (updatedQualifications.has(qualification)) {
        updatedQualifications.delete(qualification);
      } else {
        updatedQualifications.add(qualification);
      }
      return updatedQualifications;
    });
  }, []);

  // Function to extract the unique values from the leads data
  const extractCountriesFromLeads = useCallback(() => {
    const countries = new Set();

    leads.data.forEach((lead) => {
      // If no category is selected, include all leads
      if (
        lead.country_name &&
        (selectedCategories.size === 0 ||
          (lead.softwareinterest && [...selectedCategories].some((category) => lead.softwareinterest.includes(category))))
      ) {
        countries.add(decodeHtmlEntities(lead.country_name));
      }
    });

    return Array.from(countries);
  }, [leads.data, selectedCategories]);


  const extractIndustrySectorsFromLeads = useCallback(() => {
    const industrySectors = new Map();

    leads.data.forEach((lead) => {
      // If no category is selected, include all leads
      if (
          lead.industry_sector &&
          (selectedCategories.size === 0 ||
              (lead.softwareinterest && [...selectedCategories].some((category) => lead.softwareinterest.includes(category))))
      ) {
        const normalised = String(lead.industry_sector).trim().toLowerCase();
        if (!industrySectors.has(normalised)) {
          industrySectors.set(normalised, decodeHtmlEntities(lead.industry_sector));
        }
      }
    });

    return Array.from(industrySectors.entries()).map(([normalised, original]) => ({original, normalised}));
  }, [leads.data, selectedCategories]);


  const extractSpecialtiesFromLeads = useCallback(() => {
    const specialties = new Set();
    leads.data.forEach((lead) => {
      if (lead.practicespecialty) {
        specialties.add(lead.practicespecialty);
      }
    });
    return Array.from(specialties);
  }, [leads.data]);

    const extractProblemsFromLeads = useCallback(() => {
    const problems = new Set();
    leads.data.forEach((lead) => {
        if (lead.problem) {
            problems.add(lead.problem);
        }
    });
    return Array.from(problems);
    }, [leads.data]);

  const extractQualificationTypesFromLeads = useCallback(() => {
    const qualificationTypes = new Set();
    leads.data.forEach((lead) => {
      if (lead.qualification_type) {
        qualificationTypes.add(lead.qualification_type);
      }
    });
    return Array.from(qualificationTypes);
  }, [leads.data]);

  // Function to remove values from selected filters
  const removeCountry = useCallback((country) => {
    setSelectedCountries((prev) => {
      const updatedCountries = new Set(prev);
      updatedCountries.delete(country);
      return updatedCountries;
    });
  }, []);

  const removeIndustrySector = useCallback((industrySector) => {
    setSelectedIndustrySectors((prev) => {
      const updatedIndustrySectors = new Set(prev);
      updatedIndustrySectors.delete(industrySector);
      return updatedIndustrySectors;
    });
  }, []);

  const removeSpecialty = useCallback((specialty) => {
    setSelectedSpecialties((prev) => {
      const updatedSpecialties = new Set(prev);
      updatedSpecialties.delete(specialty);
      return updatedSpecialties;
    });
  }, []);

    const removeProblem = useCallback((problem) => {
    setSelectedProblems((prev) => {
        const updatedProblems = new Set(prev);
        updatedProblems.delete(problem);
        return updatedProblems;
    });
    }, []);

  const removeQualification = useCallback((qualification) => {
    setSelectedQualificationTypes((prev) => {
      const updatedQualifications = new Set(prev);
      updatedQualifications.delete(qualification);
      return updatedQualifications;
    });
  }, []);

  // Memoized list of filters
  const countries = useMemo(() => extractCountriesFromLeads(), [extractCountriesFromLeads]);
  const industrySectors = useMemo(() => extractIndustrySectorsFromLeads(), [extractIndustrySectorsFromLeads]);
  const specialties = useMemo(() => extractSpecialtiesFromLeads(), [extractSpecialtiesFromLeads]);
    const problems = useMemo(() => extractProblemsFromLeads(), [extractProblemsFromLeads]);

  // Filter options
  const countryOptions = countries.map((country) => (
      <CustomCheckbox
          key={country}
          label={country}
          value={country}
          checked={selectedCountries.has(country)}
          onChange={countryChanged}
      />
  ));

  const industrySectorOptions = industrySectors.map(({ original, normalised }) => (
      <CustomCheckbox
          key={normalised}
          label={original}
          value={normalised}
          checked={selectedIndustrySectors.has(normalised)}
          onChange={industrySectorChanged}
      />
  ));

  const specialtyOptions = specialties.map((specialty) => (
      <CustomCheckbox
          key={specialty}
          label={specialty}
          value={specialty}
          checked={selectedSpecialties.has(specialty)}
          onChange={specialtyChanged}
      />
  ));

  const problemOptions = problems.map((problem) => (
      <CustomCheckbox
          key={problem}
          label={problem}
          value={problem}
          checked={selectedProblems.has(problem)}
          onChange={problemChanged}
      />
  ));

  const qualificationOptions = ['Telephone Qualified', 'MQL+'].map((type) => (
      <CustomCheckbox
          key={type}
          label={type}
          value={type}
          checked={selectedQualificationTypes.has(type)}
          onChange={qualificationTypeChanged}
      />
  ));

  // Filter dropdowns
  const countryDropdown = (
    <div
      ref={countryDropdownRef}
      className="filter-dropdown"
      style={{display: showCountryDropdown ? 'block' : 'none', backgroundColor: '#F7F6F6'}}
    >
      {countryOptions}
    </div>
  );

  const industrySectorDropdown = (
    <div
      ref={industrySectorDropdownRef}
      className="filter-dropdown"
      style={{display: showIndustrySectorDropdown ? 'block' : 'none', backgroundColor: '#F7F6F6'}}
    >
      {industrySectorOptions}
    </div>
  );

  const specialtyDropdown = (
    <div
      ref={specialtyDropdownRef}
      className="filter-dropdown"
      style={{display: showSpecialtyDropdown ? 'block' : 'none', backgroundColor: '#F7F6F6'}}
    >
      {specialtyOptions}
    </div>
  );

    const problemDropdown = (
    <div
        ref={problemDropdownRef}
        className="filter-dropdown"
        style={{display: showProblemDropdown ? 'block' : 'none', backgroundColor: '#F7F6F6'}}
    >
        {problemOptions}
    </div>
    );

  const qualificationDropdown = (
    <div
      ref={qualificationDropdownRef}
      className="filter-dropdown"
      style={{display: showQualificationDropdown ? 'block' : 'none', backgroundColor: '#F7F6F6'}}
    >
      {qualificationOptions}
    </div>
  );

  // Filters
  const filterByCountry = (
    <div className="l-leads-filter">
      <label htmlFor="selectCountry" className="filter-by">
        {/*Filter by:*/}
        <div style={{position: 'relative', display: 'inline-block'}}>
          <button
            ref={dropdownToggleButtonRef}
            className="l-leads-filters-control control control--select"
            onClick={toggleCountryDropdown}
          >
            Country <span className={showCountryDropdown ? 'arrow-up' : 'arrow-down'}></span>
          </button>
          {countryDropdown}
        </div>
      </label>
    </div>
  );

  // Filter by industry sector
  const filterByIndustrySector = (
    <div className="l-leads-filter">
      <label htmlFor="selectIndustrySector" className="filter-by">
        {/*Filter by:*/}
        <div style={{position: 'relative', display: 'inline-block'}}>
          <button
            ref={industrySectorDropdownToggleButtonRef}
            className="l-leads-filters-control control control--select"
            onClick={toggleIndustrySectorDropdown}
          >
            Industry <span className={showIndustrySectorDropdown ? 'arrow-up' : 'arrow-down'}></span>
          </button>
          {industrySectorDropdown}
        </div>
      </label>
    </div>
  );

  // Filter by specialty
  const filterBySpecialty = (
    <div className="l-leads-filter">
      <label htmlFor="selectSpecialty" className="filter-by">
        {/*Filter by:*/}
        <div style={{position: 'relative', display: 'inline-block'}}>
          <button
            ref={specialtyDropdownToggleButtonRef}
            className="l-leads-filters-control control control--select"
            onClick={toggleSpecialtyDropdown}
          >
            Specialty <span className={showSpecialtyDropdown ? 'arrow-up' : 'arrow-down'}></span>
          </button>
          {specialtyDropdown}
        </div>
      </label>
    </div>
  );

    // Filter by problem
    const filterByProblem = (
    <div className="l-leads-filter">
        <label htmlFor="selectProblem" className="filter-by">
            {/*Filter by:*/}
            <div style={{position: 'relative', display: 'inline-block'}}>
                <button
                    ref={problemDropdownToggleButtonRef}
                    className="l-leads-filters-control control control--select"
                    onClick={toggleProblemDropdown}
                >
                    Problem <span className={showProblemDropdown ? 'arrow-up' : 'arrow-down'}></span>
                </button>
                {problemDropdown}
            </div>
        </label>
    </div>
    );

  const filterByQualification = (
    <div className="l-leads-filter">
      <label className="filter-by">
        <div style={{position: 'relative', display: 'inline-block'}}>
          <button
            ref={qualificationDropdownRef}
            onClick={toggleQualificationDropdown}
            className="l-leads-filters-control control control--select"
          >
            Lead Type <span className={showQualificationDropdown ? 'arrow-up' : 'arrow-down'}></span>
          </button>
          {qualificationDropdown}
        </div>
      </label>
    </div>
  );

  // Dynamic title based on page
  let pageTitle = '';
  switch (page) {
    case 'tq':
      pageTitle = 'Your TQ (Telephone Qualified) Leads';
      break;
    case 'purchased':
      pageTitle = 'Your Purchased Leads';
      break;
    case 'available':
      pageTitle = 'Your Available Leads';
      break;
    case 'mql-plus':
      pageTitle = 'Your MQL+ Leads';
      break;
    default:
      pageTitle = 'Your Leads';
  }

  // Rendering the leads based on filters
  const leadsToRender = useMemo(() => {
    if (leads.loading) return [];

    return leads.data
      .filter((lead) => {
        if (page === 'opportunities' && lead.type === 'opportunity') {
          return true; // Display opportunity leads
          // } else if (page === 'opportunities' && lead.qualification_type === 'mql_plus') {
          //   return false; // Exclude MQL+ leads
        }
        // Filter by country
        // Filter opportunity leads on the "opportunities" page
        if (page === 'opportunities' && lead.type !== 'opportunity') return false;

        // Filter by market
        // if (page === 'available' && selectedMarket > 0) {
        //   return lead.market_id === selectedMarket;
        // }

        // Apply country filter if any country is selected
        if (selectedCountries.size > 0 && !selectedCountries.has(lead.country_name)) return false;
        // Apply qualification type filter
        if (
          selectedQualificationTypes.size > 0 &&
          !selectedQualificationTypes.has(
            lead.qualification_type === 'phone_qualified' ? 'Telephone Qualified' : 'MQL+'
          )
        ) {
          return false;
        }
        // Apply industry sector filter if any sector is selected
        const industryValue = lead.market_name === 'HRMS' ? lead.sagecampaignindustries : lead.industry_sector;
        const normalisedSector = industryValue ? String(industryValue).trim().toLowerCase() : '';

        if (selectedIndustrySectors.size > 0 && !selectedIndustrySectors.has(normalisedSector)) return false;
        // Filter by problem
        if (selectedProblems.size > 0 && !selectedProblems.has(lead.problem)) return false;
        // Filter by specialty
        if (selectedSpecialties.size > 0 && !selectedSpecialties.has(lead.practicespecialty)) return false;
        // Filter by category
        if (selectedCategories.size > 0) {
          const leadCategories = lead.softwareinterest ? lead.softwareinterest.split(',') : ['Uncategorized'];
          const decodedCategories = leadCategories.map(category => decodeHtmlEntities(category.trim()));

          // Ensure that the decoded categories are compared with selected categories
          if (!decodedCategories.some(decodedCategory => selectedCategories.has(decodedCategory))) return false;
        }
        // If no filters prevent the lead from being displayed, include it
        return true;
      })
      .map((lead) => {
        if (lead.type === 'opportunity') {
          return <OpportunityCard onClick={viewLead} key={lead.id} lead={lead} page={page} currency={team.currency}/>;
        }
        return <AcceptedLeadCard onClick={viewLead} key={lead.id} lead={lead} page={page} currency={team.currency} />;
      });
  }, [leads, selectedCountries, selectedQualificationTypes, selectedIndustrySectors, selectedSpecialties, selectedProblems, selectedCategories, selectedMarket, page, viewLead]);

  // Determine columns based on the team market
  const columns = team.market === 'EHR'
    ? ['Practitioners', 'Specialty', 'Problem', 'Received', 'Price']
    : ['Revenue', 'Industry', 'Problem', 'Received', 'Price'];

  let moreButtonLabel = '';
  let moreButtonStyle = {};

  if (leads.loading || leads.loading_more) {
    moreButtonLabel = 'Loading';
  } else if (leads.num_leads_available < 1) {
    moreButtonLabel = 'All Leads Loaded';
  } else if (leads.num_leads_available < 50) {
    moreButtonLabel = 'Load More (' + leads.num_leads_available + ' available)';
  } else {
    moreButtonLabel = 'Load More (50+ available)';
  }

  if (leads.loading || leads.loading_more || leads.num_leads_available < 1) {
    moreButtonStyle = {
      cursor: 'default',
      pointerEvents: 'none',
    }
  }

  const navigateToArchived = () => {
    history.push('/leads/archived');
  }

  let softwareComponent = null;

  /*
  if (page === 'available') {
    softwareComponent = (
      <SoftwareMarketComponent
        key={team}
        team={team}
        leads={leads.data}
        selectedMarket={selectedMarket}
        onMarketChange={setSelectedMarket}
      />
    );
  } else if (team.market !== 'EHR') {
    softwareComponent = (
      <SoftwareInterestComponent
        onCategoryChange={handleCategoryChange}
        fetchLeadsData={leads.data}
      />
    );
  }
  */

  // Rendering FEED component
  return (
    <section className="l-wrapper-container">
      <header className="l-leads-header flex-x">
        <h1 className="t-primary-header flex-1">{pageTitle}</h1>
        {page === 'purchased' ? (
          <div className="btn button-alt" onClick={navigateToArchived}>View Archived Leads</div>
        ) : null}
      </header>
      <TeamLeadPurchaseLimit />
      {softwareComponent}
      <div className="l-leads-filters">
        <span className="filter-by" style={{paddingTop: '5px'}}>Filter:</span>
        {filterByQualification}
        {team.market !== 'EHR' && filterByIndustrySector}
        {team.market === 'EHR' && filterBySpecialty}
        {filterByProblem}
        {filterByCountry}
      </div>
      <div className="dynamic-filtering">
        {(selectedCountries.size > 0 || selectedIndustrySectors.size > 0 || selectedSpecialties.size > 0 || selectedQualificationTypes.size > 0 || selectedProblems.size > 0) &&
          <span className="filter-title">Filters:</span>}
        {Array.from(selectedCountries).map((country) => (
          <span key={country} className="filter-pill">
            <span>{country}</span>
            <span className="filter-close-btn" onClick={() => removeCountry(country)}>
              x
            </span>
          </span>
        ))}
        {Array.from(selectedIndustrySectors).map((normalised) => {
          const original = industrySectors.find(({normalised: norm}) => norm === normalised)?.original;
          return (
            <span key={normalised} className="filter-pill">
                          <span>{original}</span>
    <span className="filter-close-btn" onClick={() => removeIndustrySector(normalised)}>x</span>
    </span>
          );
        })}
        {Array.from(selectedSpecialties).map((specialty) => (
          <span key={specialty} className="filter-pill">
                      <span>{specialty}</span>
    <span className="filter-close-btn" onClick={() => removeSpecialty(specialty)}>x</span>
    </span>
        ))}
        {Array.from(selectedProblems).map((problem) => (
            <span key={problem} className="filter-pill">
                        <span>{problem}</span>
                        <span className="filter-close-btn" onClick={() => removeProblem(problem)}>x</span>
                    </span>
        ))}
        {Array.from(selectedQualificationTypes).map((qualification) => (
          <span key={qualification} className="filter-pill">
                      <span>{qualification}</span>
                      <span className="filter-close-btn" onClick={() => removeQualification(qualification)}>x</span>
                  </span>
        ))}
      </div>

      <div className="l-leads-data">
        {leadsToRender.length > 0 && (
          <div className="l-leads-data-table">
            <DataList
              leads={leadsToRender}
              mainHeader={[' ', ' ', feedTitle]}
              headers={columns}
            />
          </div>
        )}
        <button onClick={loadMoreLeads} className="btn btn--neutral btn--block" style={moreButtonStyle}>
          {moreButtonLabel}
        </button>
      </div>
      <CmsFooter/>
      <InlineLeadViewer />
    </section>
  );
};

Feed.propTypes = {
  leads: PropTypes.object,
  page: PropTypes.string.isRequired,
  lead_id: PropTypes.string,
  getLeads: PropTypes.func,
  getMoreLeads: PropTypes.func,
  showFeaturesList: PropTypes.func,
  hideFeaturesList: PropTypes.func,
  viewLead: PropTypes.func,
  team: PropTypes.object,
  opportunityCount: PropTypes.object,
  match: PropTypes.object,
  features: PropTypes.object,
};

Feed.defaultProps = {
  leads: {},
  getLeads: () => {
  },
  getMoreLeads: () => {
  },
  viewLead: () => {
  },
  showFeaturesList: () => {
  },
  hideFeaturesList: () => {
  },
  team: {},
  opportunityCount: {},
  match: null,
  features: {data: [], feature_list_hidden: false},
};

const mapStateToProps = (state) => ({
  leads: state.leads,
  team: state.teams.all.find((team) => team.id === state.teams.currentTeam),
  opportunityCount: state.opportunityCount,
  features: state.features,
});

const mapDispatchToProps = (dispatch) => ({
  getLeads: (params) => dispatch(getLeads(params)),
  getMoreLeads: (params) => dispatch(getMoreLeads(params)),
  showFeaturesList: () => dispatch(showFeatureList()),
  hideFeaturesList: () => dispatch(hideFeatureList()),
  getFeatures: (params) => dispatch(getFeatures(params)),
  viewLead: (id, direct = false, archive = false) => dispatch(viewLead(id, direct, archive)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FeedRouter));
